'use client'

import { AnimatePresence, motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import { ITranslationKey } from '@/components/t'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { COLORS } from '@/utils/styles/colors'
import { useMobile } from '@/utils/window/use-mobile'
import { ICommonProps } from '../common/common-props'

type IJobsApplicationCard = {
    title: string
    time: string
    description: string
}

export const JobsApplicationCards = ({ tBaseKey }: ICommonProps) => {
    const t = useTranslations()
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    const isMobile = useMobile()

    const items = useMemo<IJobsApplicationCard[]>(() => {
        try {
            const rawItems = t.raw((tBaseKey + '.items') as ITranslationKey)

            // biome-ignore lint/suspicious/noExplicitAny: biome migration
            return rawItems.map((item: any) => ({
                title: item.title,
                time: item.time,
                description: item.description,
            }))
        } catch (error) {
            // biome-ignore lint/suspicious/noConsole: intentional error logging
            console.log('error', error)
            return null
        }
    }, [t, tBaseKey])

    if (!items?.length) return null

    const toggleOpen = (index: number) => {
        setOpenIndex((prev) => (prev === index ? null : index))
    }

    return (
        <div className="flex flex-col md:flex-row md:pl-28 2xl:pl-0">
            {items.map((item, index) => {
                const isOpen = openIndex === index

                return (
                    <div
                        key={`application-card-${index}-${item.title}`}
                        className="border border-gray-100 shadow-md p-4 rounded-s my-2 mx-3 overflow-hidden flex-1 min-w-[272px] md:max-w-[272px] bg-white"
                    >
                        <div className="flex items-center" onClick={() => toggleOpen(index)}>
                            <div className="pb-4 flex flex-col gap-3 w-full">
                                <span className="inline-flex items-center justify-center text-[20px] md:text-[22px] w-8 h-8 md:w-10 md:h-10 bg-neotaste font-semibold rounded-full">
                                    {index + 1}
                                </span>
                                <h2 className="text-[20px] md:text-[22px]">{item.title}</h2>
                                <p className="text-gray-500 text-[14px]">{item.time}</p>
                            </div>
                            {isMobile && (
                                <div>
                                    <motion.div
                                        initial={{ rotate: isOpen ? 270 : 90 }}
                                        animate={{ rotate: isOpen ? 270 : 90 }}
                                    >
                                        <SvgOutlinedChevronRight
                                            color={COLORS.black}
                                            size={24}
                                            style={{ position: 'relative', top: '-1px' }}
                                        />
                                    </motion.div>
                                </div>
                            )}
                        </div>
                        <AnimatePresence initial={false} mode="popLayout">
                            {(isOpen || !isMobile) && (
                                <motion.div
                                    initial={{ opacity: 1 }}
                                    animate={{ opacity: 1, transition: { duration: 0 } }}
                                    exit={{ opacity: 0, transition: { duration: 0.2 } }}
                                    className="flex flex-col gap-6 pb-8"
                                >
                                    <hr className="w-full border-t border-gray-200 mb-1" />
                                    <p className="text-[14px]">{item.description}</p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )
            })}
            {!isMobile && <div className="md:pl-28 2xl:pl-0" />}
        </div>
    )
}
